/**
 * FullAIAssistantMode - Full-screen AI assistant
 * Embeds ConversationalCoach component with workspace discovery session
 */

import { useEffect, useState } from 'react'
import type { WorkspaceContext } from '@/stores/workspaceStore'
import { ConversationalCoach } from '@/components/discovery/ConversationalCoach'
import { useAuthStore } from '@/stores/authStore'
import { useProjectStore } from '@/stores/projectStore'
import { discoveryApi } from '@/lib/api/client'
import { Loader2 } from 'lucide-react'

interface FullAIAssistantModeProps {
  context: WorkspaceContext
}

export function FullAIAssistantMode({ context }: FullAIAssistantModeProps) {
  const { currentAccount } = useAuthStore()
  const { currentProject } = useProjectStore()
  const [sessionId, setSessionId] = useState<string | undefined>()
  const [isLoading, setIsLoading] = useState(true)

  // Get or create workspace discovery session
  useEffect(() => {
    if (!currentAccount || !currentProject) return

    const initSession = async () => {
      try {
        // Try to get existing sessions for this project
        const sessions = await discoveryApi.listSessions(currentAccount.id, currentProject.id)

        if (sessions.length > 0) {
          // Use the most recent session
          setSessionId(sessions[0].id)
        } else {
          // Create a new session titled "Workspace Chat"
          const newSession = await discoveryApi.createSession(
            currentAccount.id,
            currentProject.id,
            { title: 'Workspace Chat', mode: 'deep' }
          )
          setSessionId(newSession.id)
        }
      } catch (error) {
        console.error('Failed to initialize discovery session:', error)
      } finally {
        setIsLoading(false)
      }
    }

    initSession()
  }, [currentAccount, currentProject])

  if (!currentAccount || !currentProject) {
    return (
      <div className="flex items-center justify-center h-full">
        <p className="text-sm text-text-muted">
          Please select an account and project
        </p>
      </div>
    )
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="w-6 h-6 animate-spin text-warm-500" />
      </div>
    )
  }

  return (
    <div className="h-full bg-warm-50">
      <ConversationalCoach
        accountId={currentAccount.id}
        projectId={currentProject.id}
        sessionId={sessionId}
      />
    </div>
  )
}
